/**
 * UI-3.5: Action Feedback Toast
 *
 * Inline toast that appears after action execution.
 * Animates in, auto-dismisses after 4s.
 */

import type { ActionFeedback } from '../../hooks/useActionDispatch';

interface ActionToastProps {
  feedback: ActionFeedback | null;
  onDismiss: () => void;
}

const VARIANT_STYLES: Record<string, string> = {
  success: 'bg-emerald-900/90 border-emerald-700 text-emerald-200',
  error: 'bg-rose-900/90 border-rose-700 text-rose-200',
};

const VARIANT_ICONS: Record<string, string> = {
  success: '✓',
  error: '✕',
};

export function ActionToast({ feedback, onDismiss }: ActionToastProps) {
  if (!feedback) return null;

  const styles = VARIANT_STYLES[feedback.variant] ?? VARIANT_STYLES.success!;
  const icon = VARIANT_ICONS[feedback.variant] ?? '✓';

  const isError = feedback.variant === 'error';

  return (
    <div role={isError ? 'alert' : 'status'} aria-live={isError ? 'assertive' : 'polite'} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-in-right">
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg backdrop-blur-sm ${styles}`}
      >
        <span className="text-sm font-medium">{icon}</span>
        <span className="text-sm">{feedback.message}</span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="ml-2 opacity-60 hover:opacity-100 text-xs transition-opacity"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
